import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../../lib/theme';
import { v1Users, type V1User } from '../../../../lib/v1';
import { v2Matching } from '../../../../lib/matching';
import { MatchScoreBadge } from '../../../../components/matching/MatchScoreBadge';
import type { BkPickerProps } from './types';

export function BkPicker({ dark, pbUserId, value, onChange, role = 'BK' }: BkPickerProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  // i18n-Präfix je Rolle: BK-Texte (Default) oder PB-Texte ("PB auswählen" …).
  const K = role === 'PB' ? 'pb' : 'bk';
  const [bks, setBks] = useState<V1User[]>([]);
  // Match-Score je Kandidat (aus dem Matching-Modul) — steuert Sortierung + Badge.
  const [scores, setScores] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // ALLE aktiven Nutzer der gewählten Rolle laden (konto===4, status!==2 =
    // nicht storniert) — über alle Seiten hinweg, damit das Dropdown vollständig ist.
    (async () => {
      const all: V1User[] = [];
      let page = 1;
      let lastPage = 1;
      do {
        // konto MUSS ein Array sein: der v1-Controller macht daraus ein
        // whereIn('konto', $value). Als Skalar ('4') kommt bei Laravel
        // filters[konto]=4 an → whereIn mit String → 500, der Catch unten
        // schluckt es und das Dropdown blieb leer („keine Betreuungen
        // gefunden", gemeldet 18.09.2026).
        const res = await v1Users.byRole(role, { page, perPage: 200, filters: { konto: ['4'] } });
        all.push(...(res.data as V1User[]));
        lastPage = res.last_page;
        page += 1;
      } while (page <= lastPage && page <= 25); // Sicherheitskappe: max. 5000 BKs
      if (!alive) return;
      const sorted = all
        .filter(u => u.konto === 4 && u.status !== 2)
        .sort((a, b) => {
          const an = (a.fullname ?? [a.vorname, a.nachname].filter(Boolean).join(' ')).toLowerCase();
          const bn = (b.fullname ?? [b.vorname, b.nachname].filter(Boolean).join(' ')).toLowerCase();
          return an.localeCompare(bn, 'de');
        });
      setBks(sorted);
    })()
      .catch(() => { /* Liste bleibt leer — Empty-State greift */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [role]);

  // Matching-Scores für den Anker-User laden (best effort — Liste funktioniert auch ohne).
  useEffect(() => {
    let alive = true;
    v2Matching.candidatesFor(pbUserId, 100)
      .then(res => {
        if (!alive) return;
        const map: Record<number, number> = {};
        res.candidates.forEach(c => { map[c.id] = c.score; });
        setScores(map);
      })
      .catch(() => { /* ohne Scores weiter */ });
    return () => { alive = false; };
  }, [pbUserId]);

  const filtered = bks
    .filter(bk => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const name = (bk.fullname ?? [bk.vorname, bk.nachname].filter(Boolean).join(' ')).toLowerCase();
      return name.includes(q) || String(bk.id).includes(q) || (bk.email ?? '').toLowerCase().includes(q);
    })
    .sort((a, b) => (scores[b.id] ?? -1) - (scores[a.id] ?? -1));

  const selectedBk = bks.find(bk => bk.id === value);
  const selectedName = selectedBk
    ? ((selectedBk.fullname ?? [selectedBk.vorname, selectedBk.nachname].filter(Boolean).join(' ')) || `#${selectedBk.id}`)
    : null;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left', padding: '8px 12px',
          border: `1px solid ${t.border}`, borderRadius: 8,
          background: t.inputBg, color: value ? t.heading : t.muted,
          fontSize: 13, cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
        }}
      >
        {loading
          ? tr(`pflege.cares.create_${K}_loading`)
          : selectedName ?? tr(`pflege.cares.create_${K}_placeholder`)}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: t.card, border: `1px solid ${t.border}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)', marginTop: 4,
          maxHeight: 220, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '8px 10px', borderBottom: `1px solid ${t.border}` }}>
            <input
              autoFocus
              type="text"
              placeholder={tr(`pflege.cares.create_${K}_placeholder`)}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', fontSize: 12.5, padding: '6px 8px',
                border: `1px solid ${t.border}`, borderRadius: 6, background: t.inputBg,
                color: t.text, outline: 'none', fontFamily: 'Outfit,sans-serif',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading && (
              <div style={{ padding: '10px 12px', fontSize: 12, color: t.muted }}>{tr(`pflege.cares.create_${K}_loading`)}</div>
            )}
            {!loading && filtered.length === 0 && (
              <div style={{ padding: '10px 12px', fontSize: 12, color: t.muted }}>{tr(`pflege.cares.create_${K}_empty`)}</div>
            )}
            {filtered.map(bk => {
              const name = (bk.fullname ?? [bk.vorname, bk.nachname].filter(Boolean).join(' ')) || `#${bk.id}`;
              const isSelected = bk.id === value;
              return (
                <button
                  key={bk.id}
                  type="button"
                  onClick={() => { onChange(bk.id, name); setOpen(false); setSearch(''); }}
                  style={{
                    width: '100%', textAlign: 'left', padding: '9px 12px',
                    background: isSelected ? BRAND_LIGHT : 'transparent',
                    color: isSelected ? BRAND : t.text,
                    border: 'none', cursor: 'pointer', fontSize: 13,
                    fontWeight: isSelected ? 600 : 400,
                    fontFamily: 'Outfit,sans-serif',
                    borderBottom: `1px solid ${t.borderSoft}`,
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{name}</span>
                  <span style={{ marginLeft: 8, fontSize: 11, color: t.muted }}>#{bk.id}</span>
                  {scores[bk.id] != null && (
                    <span style={{ float: 'right' }}>
                      <MatchScoreBadge score={scores[bk.id]} size="sm" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
